/*
 * Copyright (c) 2021-2021. Datafood srl
 * Developed by Revo Digital - revodigital.it
 * ---
 * Author: leonardoviada
 * File: Dashboard.tsx
 * Project: mle-client
 * Committed last: 2021/11/11 @ 1844
 * ---
 */

import { ProjectCard }                             from '../components/ProjectCard';
import { Button, NonIdealState, Spinner }          from '@blueprintjs/core';
import React, { useCallback, useEffect, useState } from 'react';
import { redirect }                                from '../common/utils';
import { mleDomainGqlClient }                      from '../api/domain';
import {
  REMOVE_LABEL
}                                                  from '../api/domain/mutations.gql';
import {
  Header
}                                                  from '../components/Header';
import { useDispatch, useSelector }                from 'react-redux';
import {
  idleAll,
  runRoot
}                                                  from '../store/slices/loading-slice';
import {
  LabelPaths
}                                                  from '../paths/label.paths';
import { MleToaster, Toasts }                      from '../common/toast';
import { RootState }                               from '../store';
import { Label }                                   from '../entities';
import {
  ComingSoonCallout
}                                                  from '../components/common/callout/ComingSoonCallout';
import {
  ChangelogOverlay
}                                                  from '../components/common/overlay/ChangelogOverlay';
import {
  GET_LABEL_BY_USER_ID
}                                                  from '../api/domain/queries.gql';
import {
  CreateProjectDialog
}                                                  from '../components/editor/dialogs/create-project/CreateProjectDialog';
import {
  ProjectCreationAgent,
  ProjectCreationConfig
}                                                  from '../store/agents/ProjectCreationAgent';
import {
  ImportFromMpesDialog
}                                                  from '../components/editor/dialogs/import/ImportFromMpesDialog';

export const Dashboard = () => {
  const [ isCreatingProject, setIsCreatingProject ] = useState(false);
  const [ importVisible, setImportVisible ] = useState(false);
  const [ changelogIsOpen, setChangelogIsOpen ] = useState(false);
  const [ labels, setLabels ] = useState<Label[]>([]);
  const [ isLoadingLabels, setIsLoadingLabels ] = useState(false);
  const authData = useSelector((state: RootState) => state.authState);
  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      if (authData.user.id) {
        setIsLoadingLabels(true);
        const labelRes = await mleDomainGqlClient.query({
          query: GET_LABEL_BY_USER_ID,
          variables: {
            userId: authData.user.id
          },
          fetchPolicy: 'network-only'
        });
        setIsLoadingLabels(false);
        setLabels(labelRes.data.labels);
      }
    })();
  }, [ authData.user.id ]);

  const handleDelete = async (labelId: number) => {
    await mleDomainGqlClient.mutate({
      mutation: REMOVE_LABEL,
      variables: {
        id: labelId
      }
    });
    setLabels(labels.filter(({ id }) => id !== labelId));
    MleToaster.getInstance().show(Toasts.PROJECT_REMOVED);
  };

  const handleProjectCreation = async (config: ProjectCreationConfig) => {
    // Create project
    const projectId = await ProjectCreationAgent.createProject(config);

    if (projectId > 0) {
      dispatch(runRoot());
      setTimeout(() => {
        redirect(`${ LabelPaths.ROOT }/${ projectId }`);
        dispatch(idleAll());
        MleToaster.getInstance().show(Toasts.PROJECT_CREATED);
      }, 2000);
    } else MleToaster.getInstance().show(Toasts.PROJECT_ERROR);
  };

  const handleLabelImported = useCallback((label: Label) => {
    setImportVisible(false);
    setLabels([ ...labels, label ]);
    MleToaster.getInstance().show(Toasts.PROJECT_IMPORTED);
  }, [ setImportVisible, labels, setLabels ]);

  return (
    <div>
      <div className="max-w-7xl p-4 mx-auto">
        <Header setChangelogIsOpen={ setChangelogIsOpen }/>
        <div className="my-3 p-3">
          <ComingSoonCallout
            nextFeatures={ [ 'Libreria template di design etichette', 'Impostazioni profilo utente', 'Esportazione rapida da dashboard', 'Anteprima da dashboard', 'Per consentire l\'utilizzo delle risorse a tutti gli utenti, nella versione attuale non è consentita l\'eliminazione' ] }/>
          <div className="flex lex-row justify-between">
            <div className="">
              <div className="text-muted">Area di lavoro
                di <strong>{ authData.user.company?.name }</strong></div>
              <h3 className="bp4-heading my-2 mb-2">Progetti
                di { authData.user.firstName }</h3>
            </div>
            <div className="space-x-2">
              <Button
                intent="none"
                icon="cloud-upload"
                onClick={ () => setImportVisible(true) }>
                Importa
              </Button>
              <Button
                intent="primary"
                icon="plus"
                onClick={ () => setIsCreatingProject(true) }
              >
                Nuovo progetto
              </Button>
            </div>
          </div>
          {
            <div
              className="grid grid-cols-3 gap-2 sm:grid-cols-2">
              { labels && labels.map(label => (
                <ProjectCard
                  key={ label.creationDate }
                  label={ label }
                  onDelete={ handleDelete }
                />
              )) }
            </div>
          }
          {
            (!labels || labels.length === 0) && !isLoadingLabels &&
            <NonIdealState
              className="mt-5 p-4 bg-white bg-opacity-5 rounded-sm"
              icon="projects"
              title="Nessun progetto"
              description={ <div className="bp4-text-muted -mt-3">Non abbiamo
                trovato nessun progetto, creane subito uno con il pulsante che
                segue</div> }
              action={ <Button
                intent="primary"
                icon="plus"
                onClick={ () => setIsCreatingProject(true) }
              >
                Nuovo progetto
              </Button> }
            />
          }
          {
            (!labels || labels.length === 0) && isLoadingLabels &&
            <div className="mt-5 p-4 bg-white bg-opacity-5 rounded-sm">
              <Spinner size={ 30 } className="mx-auto"/>
            </div>
          }
        </div>
      </div>
      <CreateProjectDialog
        isOpen={ isCreatingProject }
        onClose={ () => setIsCreatingProject(false) }
        onProjectCreate={ handleProjectCreation }
      />
      <ChangelogOverlay
        handleClose={ () => setChangelogIsOpen(false) }
        isOpen={ changelogIsOpen }
      />
      {
        importVisible &&
        <ImportFromMpesDialog
          onImportSuccessful={ handleLabelImported }
          isOpen={ importVisible }
          onClose={ () => setImportVisible(false) }
        />
      }
    </div>
  );
};
